import { useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  AddBoxIcon,
  ArrowUpIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Column,
  ConfirmationDialog,
  Dialog,
  EmptyState,
  ExternalLinkIcon,
  FormField,
  IconBox,
  LinkIcon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Paragraph,
  PlusIcon,
  Row,
  Text,
  TextInput,
  useToast,
} from "@hightouch/htui";
import { Controller } from "react-hook-form";
import { z } from "zod";

import { DeleteButton } from "src/components/delete-button";
import { Form, useHightouchForm } from "src/components/form";
import {
  AmpProjectLinkKind,
  type AmpProjectLinkSourceFragment,
} from "src/graphql";
import {
  type AttachmentFacts,
  attachmentFacts,
} from "src/pages/projects/attachment-facts";
import { isPendingId } from "src/pages/projects/board-cache";
import { useBoard } from "src/pages/projects/board-context";
import { AddContentPicker } from "src/pages/projects/components/add-content-picker";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import { MAX_UPLOAD_BYTES } from "src/pages/projects/library/canvas/canvas-uploads";
import { useUploadToLibrary } from "src/pages/projects/library/files/use-upload-file";
import {
  compareLinkKinds,
  type DescribedLink,
  describeLink,
  isExternalHref,
  KIND_ICONS,
} from "src/pages/projects/links";
import {
  type AttachmentLink,
  attachmentSummary,
  NO_ATTACHMENTS_MESSAGE,
} from "src/pages/projects/project-attachments";
import { Link } from "src/router";

type Props = {
  projectId: string;
  taskId?: string;
  links: AttachmentLink[];
};

type AttachmentsProps = Pick<Props, "links"> & {
  /** Leaves out the remove buttons, for a preview. */
  isReadOnly?: boolean;
};

export const Attachments = ({
  links,
  isReadOnly = false,
}: AttachmentsProps) => {
  const { projects } = useBoard();
  // Sorted by the stage of the work; within a kind, the given order holds.
  const rows = links
    .map((link) => ({
      link,
      described: describeLink(link),
      facts: attachmentFacts(link),
    }))
    .sort((a, b) => compareLinkKinds(a.described.kind, b.described.kind));

  if (rows.length === 0) {
    return (
      <EmptyState
        variant="minimal"
        title="No attachments yet"
        message={NO_ATTACHMENTS_MESSAGE}
      />
    );
  }
  return (
    <Column
      borderWidth="hairline"
      borderColor="base.border"
      borderRadius="lg"
      bg="white"
      overflow="hidden"
    >
      {rows.map(({ link, described, facts }) => (
        <AttachmentRow
          key={link.id}
          link={described}
          facts={facts}
          sourceTask={link.source_task}
          // A link still being added has no row to remove yet.
          onRemove={
            isReadOnly || isPendingId(link.id)
              ? undefined
              : async () => projects.unlink(link.id)
          }
        />
      ))}
    </Column>
  );
};

const ATTACHMENT_TYPES = [
  "library",
  "canvas",
  "ad",
  "email",
  "html",
  "doc",
  "slides",
  "file",
  "audience",
  "sync",
] as const;

// Library files attach by their file id; everything else by its own id.
const LINK_KIND_OF_CONTENT = {
  library: AmpProjectLinkKind.File,
  canvas: AmpProjectLinkKind.File,
  ad: AmpProjectLinkKind.Ad,
  email: AmpProjectLinkKind.Email,
  html: AmpProjectLinkKind.File,
  doc: AmpProjectLinkKind.File,
  slides: AmpProjectLinkKind.File,
  file: AmpProjectLinkKind.File,
  audience: AmpProjectLinkKind.Audience,
  sync: AmpProjectLinkKind.Sync,
} satisfies Record<(typeof ATTACHMENT_TYPES)[number], AmpProjectLinkKind>;

/** Attaches work from Hightouch through the picker, a file from the computer, or a web link. */
export const AddAttachmentButton = ({
  projectId,
  taskId,
  size = "sm",
  shape,
  label,
}: Omit<Props, "links"> & {
  size?: "sm" | "md";
  shape?: "default" | "circle";
  /** Spelled out in place of the plus and "Attachment", where the button stands on its own, as in an empty state. */
  label?: string;
}) => {
  const [adding, setAdding] = useState<"picker" | "link">();
  const [isUploading, setIsUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadToLibrary = useUploadToLibrary();
  const { projects } = useBoard();
  const { toast } = useToast();
  const attachFiles = async (files: File[]) => {
    const tooLarge = files.filter((file) => file.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      toast({
        id: "attach-upload-size",
        variant: "error",
        title: "File is too large",
        message: `${tooLarge.map((file) => file.name).join(", ")}: choose files that are 1 GB or smaller.`,
      });
    }
    setIsUploading(true);
    try {
      for (const file of files.filter((f) => f.size <= MAX_UPLOAD_BYTES)) {
        const id = await uploadToLibrary(file);
        await projects.attach({
          projectId,
          taskId,
          target: { kind: AmpProjectLinkKind.File, id },
        });
      }
    } catch (error) {
      toast({
        id: "attach-upload",
        variant: "error",
        title: "Couldn't upload the file",
        message: error instanceof Error ? error.message : "Try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };
  return (
    <>
      <Menu>
        <MenuButton
          size={size}
          shape={shape}
          icon={label ? undefined : PlusIcon}
          isLoading={isUploading}
        >
          {label ?? "Attachment"}
        </MenuButton>
        <MenuList>
          <MenuItem icon={AddBoxIcon} onClick={() => setAdding("picker")}>
            Add from Hightouch
          </MenuItem>
          <MenuItem
            icon={ArrowUpIcon}
            onClick={() => fileInput.current?.click()}
          >
            Upload a file
          </MenuItem>
          <MenuItem icon={LinkIcon} onClick={() => setAdding("link")}>
            From URL
          </MenuItem>
        </MenuList>
      </Menu>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        aria-label="Upload a file"
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = "";
          if (files.length > 0) void attachFiles(files);
        }}
      />
      {adding === "picker" && (
        <AddContentPicker
          title="Add from Hightouch"
          types={ATTACHMENT_TYPES}
          multiple
          confirmLabel={(count) =>
            count > 1 ? `Add ${count} attachments` : "Add attachment"
          }
          onClose={() => setAdding(undefined)}
          onConfirm={(selection) =>
            projects.attach({
              projectId,
              taskId,
              target: {
                kind: LINK_KIND_OF_CONTENT[selection.type],
                id: selection.id,
              },
            })
          }
        />
      )}
      {adding === "link" && (
        <AddLinkDialog
          projectId={projectId}
          taskId={taskId}
          onClose={() => setAdding(undefined)}
        />
      )}
    </>
  );
};

type AttachmentRowProps = {
  link: DescribedLink;
  facts: AttachmentFacts;
  sourceTask?: AmpProjectLinkSourceFragment["source_task"];
  /** Left out while the link can't be removed yet. */
  onRemove?: () => Promise<void>;
};

const AttachmentRow = ({
  link,
  facts,
  sourceTask,
  onRemove,
}: AttachmentRowProps) => {
  const { icon: Icon, color } = KIND_ICONS[link.kind];
  const isExternal = link.href !== null && isExternalHref(link.href);
  const secondary = [
    ...attachmentSummary(link, facts.facts),
    ...(sourceTask
      ? [
          `From ${sourceTask.parent_task_id ? TERMS.subtask.singular : TERMS.task.singular}: ${sourceTask.title}`,
        ]
      : []),
  ].join(" · ");
  const title = (
    <Text fontWeight="medium" isTruncated display="block">
      {link.title}
    </Text>
  );
  const titleLinkProps = {
    minWidth: 0,
    _focusVisible: { boxShadow: "outline", outline: "none" },
    // The link stretches over the row, so a click anywhere but the remove button opens it.
    _after: { content: '""', position: "absolute", inset: 0 },
    title: link.title,
  } as const;

  return (
    <Row
      className="group"
      position="relative"
      align="center"
      gap={3}
      px={3}
      py={2.5}
      minWidth={0}
      borderBottomWidth="hairline"
      borderColor="base.divider"
      _last={{ borderBottomWidth: 0 }}
      _hover={link.href ? { bg: "base.lightBackground" } : undefined}
    >
      <IconBox icon={<Icon />} color={color} size="sm" />
      <Column flex={1} minWidth={0} gap={0.5}>
        <Row align="center" gap={1.5} minWidth={0}>
          {link.href ? (
            // A full URL opens in a new tab.
            <Link
              href={link.href}
              isExternal={isExternalHref(link.href)}
              {...titleLinkProps}
            >
              {title}
            </Link>
          ) : (
            <Box minWidth={0} title={link.title}>
              {title}
            </Box>
          )}
          {isExternal && (
            <Box
              as={ExternalLinkIcon}
              boxSize={3.5}
              flexShrink={0}
              color="text.tertiary"
            />
          )}
        </Row>
        {/* Wraps instead of truncating: the row's stretched link covers it, so a tooltip couldn't open. */}
        <Box noOfLines={2}>
          <Text size="sm" color="text.secondary">
            {secondary}
          </Text>
        </Box>
      </Column>
      {facts.badge && (
        <Badge
          size="sm"
          variant={facts.badge.variant}
          progress={facts.badge.progress}
        >
          {facts.badge.label}
        </Badge>
      )}
      {onRemove && (
        <Box
          position="relative"
          zIndex={1}
          flexShrink={0}
          opacity={0}
          _groupHover={{ opacity: 1 }}
          // Keyboard users need to see the button they tab to; there is no pseudo prop for this.
          sx={{ "&:has(:focus-visible)": { opacity: 1 } }}
        >
          <DeleteButton
            variant="icon"
            iconVariant="tertiary"
            action="remove"
            label="link"
            onDelete={onRemove}
          />
        </Box>
      )}
    </Row>
  );
};

const linkSchema = z.object({
  url: z
    .string()
    .trim()
    .url("Enter a full URL, starting with https://")
    .refine((value) => /^https?:\/\//i.test(value), "Use an http or https URL"),
  label: z.string().trim().max(200),
});

type LinkFormState = z.infer<typeof linkSchema>;

const AddLinkDialog = ({
  projectId,
  taskId,
  onClose,
}: {
  projectId: string;
  taskId?: string;
  onClose: () => void;
}) => {
  const { projects } = useBoard();
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const form = useHightouchForm<LinkFormState>({
    resolver: zodResolver(linkSchema),
    defaultValues: { url: "", label: "" },
    // The card shows up at once, so a toast would only repeat it.
    success: false,
    onSubmit: async ({ url, label }) => {
      projects.link({
        projectId,
        taskId,
        url,
        label: label || new URL(url).hostname,
      });
      onClose();
    },
  });

  const submit = () => {
    if (!form.formState.isSubmitting) void form.submit();
  };

  // Escape and the overlay close the dialog too, so typed text asks before it goes.
  const requestClose = () => {
    const { url, label } = form.getValues();
    if (url.trim() || label.trim()) setIsConfirmingClose(true);
    else onClose();
  };

  return (
    <Form form={form}>
      <Dialog
        isOpen
        variant="form"
        title="Add an attachment from a URL link"
        onClose={requestClose}
        actions={
          <ButtonGroup>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              isLoading={form.formState.isSubmitting}
              onClick={submit}
            >
              Add link
            </Button>
          </ButtonGroup>
        }
      >
        <Column
          gap={4}
          onKeyDown={(event) => {
            // <Form> renders no form element, so Enter submits only through this.
            if (event.key === "Enter" && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
        >
          <Controller
            control={form.control}
            name="url"
            render={({ field, fieldState }) => (
              <FormField label="URL" error={fieldState.error?.message}>
                <TextInput
                  autoFocus
                  width="100%"
                  placeholder="https://"
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="label"
            render={({ field }) => (
              <FormField
                label="Label"
                isOptional
                tip="Shown instead of the URL"
              >
                <TextInput
                  width="100%"
                  placeholder="e.g., Project brief"
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormField>
            )}
          />
        </Column>
      </Dialog>
      <ConfirmationDialog
        isOpen={isConfirmingClose}
        title="Discard link"
        cancelButtonText="Keep editing"
        confirmButtonText="Discard changes"
        variant="danger"
        onClose={() => setIsConfirmingClose(false)}
        onConfirm={onClose}
      >
        <Paragraph>Are you sure? You'll lose any unsaved changes.</Paragraph>
      </ConfirmationDialog>
    </Form>
  );
};
